// Directory of navigable Control Panel pages for global search, grouped and
// ordered like the sidebar menu (sidebar/sidebarMenu.tsx). Gates mirror the
// sidebar: a page is only offered when the user passes the same
// permission/feature checks. Keep this file in sync when sidebar routes change.
import { LucideIconName } from 'utils/lucideIcon'
import { t } from 'translations'

export interface DirectoryPage {
    title: string
    keywords: string[]
    iconName: LucideIconName
    path: (accountSlug: string) => string
    // any-of; omit for pages every user can open
    permissions?: string[]
    feature?: string
    featureOff?: string
    hideForReseller?: boolean
    // Row hint override, e.g. 'Action' for command entries. Defaults to
    // 'Page' / 'Filtered view' derived from the path.
    hint?: string
}

export interface PageResult {
    title: string
    iconName: LucideIconName
    path: string
    // Human summary of an applied scope for parameterized views (e.g.
    // "Visiting today · Canceled"). Plain pages leave it unset.
    subtitle?: string
    hint?: string
}

const accountPage =
    (path: string) =>
    (accountSlug: string): string =>
        `/account/${accountSlug}${path}`

const reportPage = (
    title: string,
    subPath: string,
    feature: string,
    iconName: LucideIconName,
    keywords: string[],
): DirectoryPage => ({
    title,
    keywords: ['reports', ...keywords],
    iconName,
    path: accountPage(`/reports/${subPath}`),
    permissions: ['access_analytics'],
    feature,
})

const getDashboardPages = (): DirectoryPage[] => [
    {
        title: t('globalSearch.pages.dashboard'),
        keywords: ['home', 'overview', 'metrics'],
        iconName: 'Home',
        path: accountPage('/dashboard/overview/'),
    },
]

const getOrderPages = (): DirectoryPage[] => [
    {
        title: t('globalSearch.pages.orders'),
        keywords: ['order list', 'transactions', 'bookings', 'sales'],
        iconName: 'ShoppingBag',
        path: accountPage('/orders/orders/'),
        permissions: ['access_orders'],
    },
    // Quick scopes: discoverable filtered views of the orders list. Typed
    // scope queries ("orders canceled paypal") are handled by scopedSearch.ts;
    // these entries surface the common ones for single-word queries.
    {
        title: t('globalSearch.pages.ordersVisitingToday'),
        keywords: ['orders today', 'today', 'visiting today', 'visits'],
        iconName: 'ShoppingBag',
        path: accountPage('/orders/orders/?dateRange=today&dateRangeType=visit&viaSearch=Visiting+today'),
        permissions: ['access_orders'],
    },
    {
        title: t('globalSearch.pages.ordersVisitingThisWeek'),
        keywords: ['orders this week', 'this week'],
        iconName: 'ShoppingBag',
        path: accountPage('/orders/orders/?dateRange=thisWeek&dateRangeType=visit&viaSearch=Visiting+this+week'),
        permissions: ['access_orders'],
    },
    {
        title: t('globalSearch.pages.ordersCancelled'),
        keywords: ['canceled orders', 'canceled', 'cancelled', 'cancellations'],
        iconName: 'ShoppingBag',
        path: accountPage(
            '/orders/orders/?dateRange=last30days&dateRangeType=visit&filter=status%3ACANCELED&viaSearch=Visiting+last+30+days+%C2%B7+Cancelled',
        ),
        permissions: ['access_orders'],
    },
    {
        title: t('globalSearch.pages.ordersOpen'),
        keywords: ['open orders', 'unpaid orders', 'pending payment'],
        iconName: 'ShoppingBag',
        path: accountPage(
            '/orders/orders/?dateRange=last30days&dateRangeType=visit&filter=status%3AOPEN&viaSearch=Visiting+last+30+days+%C2%B7+Open',
        ),
        permissions: ['access_orders'],
    },
    // Worklist for typo'd customer emails: confirmations that never arrived.
    {
        title: t('globalSearch.pages.ordersEmailBounced'),
        keywords: ['bounced', 'bounced email', 'email bounced', 'undelivered', 'wrong email', 'typo email'],
        iconName: 'MailWarning',
        path: accountPage(
            '/orders/orders/?dateRange=last30days&dateRangeType=visit&filter=email_status%3Abounced&viaSearch=Visiting+last+30+days+%C2%B7+Bounced',
        ),
        permissions: ['access_orders'],
    },
    {
        title: t('globalSearch.pages.dailySchedule'),
        keywords: ['schedule', 'visits', 'today'],
        iconName: 'CalendarDays',
        path: accountPage('/orders/daily_schedule/'),
        permissions: ['access_orders'],
        feature: 'dailySchedule',
    },
    {
        title: t('globalSearch.pages.capacityReport'),
        keywords: ['capacity', 'occupancy'],
        iconName: 'Gauge',
        path: accountPage('/orders/capacity_report/'),
        permissions: ['access_orders'],
        feature: 'is_capacity_report_enabled',
    },
    {
        title: t('globalSearch.pages.pendingReservations'),
        keywords: ['carts', 'open carts', 'reservations', 'pay later', 'group bookings', 'group booking'],
        iconName: 'ShoppingCart',
        path: accountPage('/orders/pending-reservations'),
        permissions: ['access_orders'],
        feature: 'openCartView',
    },
    // "Group bookings" in venue language: live pending reservations.
    {
        title: t('globalSearch.pages.pendingReservationsGroupBookings'),
        keywords: ['group bookings', 'group booking', 'groups', 'open reservations'],
        iconName: 'Users',
        path: accountPage(
            '/orders/pending-reservations?filter=reservation_status%3Aopen%2Cpending%2Cinitiated&viaSearch=Open+%C2%B7+Pending+%C2%B7+Initiated',
        ),
        permissions: ['access_orders'],
        feature: 'openCartView',
    },
    {
        title: t('globalSearch.pages.orderDisputes'),
        keywords: ['paypal', 'disputes', 'chargebacks'],
        iconName: 'ShieldAlert',
        path: accountPage('/orders/paypal/disputes'),
        permissions: ['partner_admin'],
        feature: 'PaypalResolutionCenter',
    },
    {
        title: t('globalSearch.pages.channels'),
        keywords: ['resellers', 'sales channels', 'distribution'],
        iconName: 'Share2',
        path: accountPage('/orders/channels/'),
        permissions: ['partner_admin'],
    },
]

const getProductPages = (): DirectoryPage[] => [
    {
        title: t('globalSearch.pages.products'),
        keywords: ['catalog', 'articles', 'tickets', 'product lists', 'categories'],
        iconName: 'Tickets',
        path: accountPage('/products/home/nested'),
        permissions: ['view_pricing_settings', 'view_product_validity'],
        hideForReseller: true,
    },
    {
        title: t('globalSearch.pages.pricingExceptions'),
        keywords: ['pricing', 'price override', 'exceptions'],
        iconName: 'BadgePercent',
        path: accountPage('/products/pricing/exceptions/list'),
        permissions: ['view_pricing_settings'],
        feature: 'Pricing',
    },
    {
        title: t('globalSearch.pages.validityExceptions'),
        keywords: ['validity', 'exceptions', 'ticket validity'],
        iconName: 'CalendarX',
        path: accountPage('/products/validity/exceptions/list'),
        permissions: ['view_product_validity'],
    },
    {
        title: t('globalSearch.pages.timeSlots'),
        keywords: ['timeslots', 'sessions'],
        iconName: 'Clock',
        path: accountPage('/products/time_slots/home/'),
        permissions: ['partner_admin'],
    },
    {
        title: t('globalSearch.pages.crowdControl'),
        keywords: ['inventory', 'availability'],
        iconName: 'SlidersHorizontal',
        path: accountPage('/products/inventory/home/'),
        permissions: ['partner_admin'],
        featureOff: 'CapacitySection',
    },
    {
        title: t('globalSearch.pages.capacity'),
        keywords: ['crowd control', 'inventory', 'availability', 'limit', 'sold out', 'pool'],
        iconName: 'SlidersHorizontal',
        path: accountPage('/products/inventory/home/'),
        permissions: ['partner_admin'],
        feature: 'CapacitySection',
    },
    {
        title: t('globalSearch.pages.apfStudio'),
        keywords: ['apf', 'product form', 'studio'],
        iconName: 'LayoutTemplate',
        path: accountPage('/products/apf_studio/'),
        permissions: ['partner_admin'],
    },
    {
        title: t('globalSearch.pages.discountRules'),
        keywords: ['discounts', 'rules', 'automatic discounts'],
        iconName: 'Percent',
        path: accountPage('/discounts/rules/home/'),
        permissions: ['partner_admin'],
        feature: 'DiscountRulesPage',
    },
]

const getVenuePages = (): DirectoryPage[] => [
    {
        title: t('globalSearch.pages.openingTimes'),
        keywords: ['venue', 'opening hours'],
        iconName: 'CalendarClock',
        path: accountPage('/venue/opening_times/'),
        permissions: ['partner_admin'],
    },
    {
        title: t('globalSearch.pages.codes'),
        keywords: ['booking codes', 'reservations', 'venue codes'],
        iconName: 'QrCode',
        path: accountPage('/venue/codes'),
        permissions: ['access_codes'],
    },
    {
        title: t('globalSearch.pages.parkMap'),
        keywords: ['venue map'],
        iconName: 'Map',
        path: accountPage('/venue/park_map'),
        feature: 'parkMap',
    },
]

const getSalesPages = (): DirectoryPage[] => [
    {
        title: t('globalSearch.pages.pointOfSale'),
        keywords: ['pos', 'terminals', 'kiosk'],
        iconName: 'Monitor',
        path: accountPage('/pos/'),
        hideForReseller: true,
    },
    {
        title: t('globalSearch.pages.memberships'),
        keywords: ['members', 'season pass', 'subscriptions'],
        iconName: 'IdCard',
        path: accountPage('/memberships/'),
        permissions: ['access_orders'],
        feature: 'memberships',
        hideForReseller: true,
    },
    // Quick scopes: discoverable filtered membership views; typed scope
    // queries ("members overdue") are handled by scopedSearch.ts.
    {
        title: t('globalSearch.pages.membershipsActive'),
        keywords: ['active members', 'active memberships'],
        iconName: 'IdCard',
        path: accountPage('/memberships/?filter=membership_status%3Aactive&viaSearch=Active'),
        permissions: ['access_orders'],
        feature: 'memberships',
        hideForReseller: true,
    },
    {
        title: t('globalSearch.pages.membershipsOverdue'),
        keywords: ['overdue', 'overdue members', 'payment overdue', 'overdue memberships'],
        iconName: 'IdCard',
        path: accountPage('/memberships/?filter=membership_status%3Aoverdue&viaSearch=Overdue'),
        permissions: ['access_orders'],
        feature: 'memberships',
        hideForReseller: true,
    },
    {
        title: t('globalSearch.pages.dynamicPricing'),
        keywords: ['pricing settings', 'price'],
        iconName: 'Sparkles',
        path: accountPage('/global_pricing_settings'),
        permissions: ['partner_admin'],
        feature: 'pricingMenu',
    },
    {
        title: t('globalSearch.pages.discounts'),
        keywords: ['discount codes', 'pools', 'vouchers', 'coupons'],
        iconName: 'Tag',
        path: accountPage('/discounts/pools'),
        permissions: ['access_codes'],
        feature: 'discountCodes',
    },
]

const getEngagePages = (): DirectoryPage[] => [
    {
        title: t('globalSearch.pages.engageTools'),
        keywords: ['wonderbar', 'checkout', 'engage'],
        iconName: 'Megaphone',
        path: accountPage('/engage/tools/home/checkout'),
        permissions: ['view_wonderbar'],
    },
    {
        title: t('globalSearch.pages.copyEditor'),
        keywords: ['copy', 'texts', 'translations', 'wording'],
        iconName: 'PenLine',
        path: accountPage('/engage/copy_editor/home/'),
        permissions: ['partner_admin'],
    },
    {
        title: t('globalSearch.pages.emailTemplates'),
        keywords: ['emails', 'templates'],
        iconName: 'Mail',
        path: accountPage('/engage/email_templates/home/'),
        permissions: ['partner_admin'],
    },
    {
        title: t('globalSearch.pages.ticketTemplates'),
        keywords: ['ticket design', 'templates'],
        iconName: 'Ticket',
        path: accountPage('/engage/ticket_templates/'),
        permissions: ['partner_admin'],
    },
    {
        title: t('globalSearch.pages.giftCardTemplates'),
        keywords: ['gift cards', 'vouchers', 'templates'],
        iconName: 'Gift',
        path: accountPage('/engage/gift_card_templates/'),
        permissions: ['partner_admin'],
    },
]

const getReportPages = (): DirectoryPage[] => [
    {
        title: t('globalSearch.pages.reports'),
        keywords: ['analytics', 'statistics'],
        iconName: 'ChartLine',
        path: accountPage('/reports/'),
        permissions: ['access_analytics'],
    },
    reportPage(t('globalSearch.pages.reportsOverview'), 'overview/', 'ReportsOverview', 'ChartLine', [
        'analytics overview',
    ]),
    reportPage(t('globalSearch.pages.salesOverviewReport'), 'sales-old', 'sale_reports', 'ChartSpline', [
        'sales',
        'revenue',
    ]),
    reportPage(t('globalSearch.pages.salesFinanceReport'), 'sales', 'SalesAndFinanceReport', 'ChartColumn', [
        'sales',
        'finance',
        'revenue',
    ]),
    // Sigma page deep links: the hash is the Sigma page nodeId, forwarded by
    // sigmaReport.tsx into the signed embed URL (page-level embed).
    reportPage(
        t('globalSearch.pages.salesFinanceReportSales'),
        'sales#uywwmBHTxq',
        'SalesAndFinanceReport',
        'ChartColumn',
        ['sales page', 'sales report', 'revenue'],
    ),
    reportPage(
        t('globalSearch.pages.salesFinanceReportPricing'),
        'sales#aMVEeJkjIJ',
        'SalesAndFinanceReport',
        'ChartColumn',
        ['pricing report', 'price analysis', 'prices'],
    ),
    reportPage(
        t('globalSearch.pages.salesFinanceReportTransactions'),
        'sales#hxYdqB_d8n',
        'SalesAndFinanceReport',
        'ChartColumn',
        ['transactions', 'payments report', 'payouts'],
    ),
    reportPage(
        t('globalSearch.pages.salesFinanceReportTax'),
        'sales#svjGAVHjhs',
        'SalesAndFinanceReport',
        'ChartColumn',
        ['tax', 'vat', 'btw'],
    ),
    reportPage(
        t('globalSearch.pages.salesFinanceReportMemberships'),
        'sales#-3v-INCVsa',
        'SalesAndFinanceReport',
        'ChartColumn',
        ['membership sales', 'memberships report', 'season passes'],
    ),
    reportPage(
        t('globalSearch.pages.salesFinanceReportFees'),
        'sales#P5-wvcKi_Q',
        'SalesAndFinanceReport',
        'ChartColumn',
        ['fees', 'booking fees'],
    ),
    reportPage(t('globalSearch.pages.operationsReport'), 'operations', 'OperationsReport', 'ChartBar', [
        'operations',
        'visits',
    ]),
    reportPage(t('globalSearch.pages.operationsReportDaily'), 'operations#CdJ3ewFsA6', 'OperationsReport', 'ChartBar', [
        'daily operations',
        'daily visits',
        'daily report',
    ]),
    reportPage(
        t('globalSearch.pages.operationsReportBarcodes'),
        'operations#Rz0HGv13l_',
        'OperationsReport',
        'ChartBar',
        ['barcodes', 'scans', 'redemptions'],
    ),
    reportPage(t('globalSearch.pages.marketingReport'), 'marketing', 'MarketingReport', 'ChartPie', [
        'marketing',
        'campaigns',
    ]),
    reportPage(
        t('globalSearch.pages.marketingReportDiscountCodes'),
        'marketing#e0OWVxSK0P',
        'MarketingReport',
        'ChartPie',
        ['discount codes', 'coupons', 'promo codes'],
    ),
    reportPage(
        t('globalSearch.pages.marketingReportEngageTools'),
        'marketing#xLIbzyMYbB',
        'MarketingReport',
        'ChartPie',
        ['engage tools', 'popups', 'wonderbar'],
    ),
    reportPage(
        t('globalSearch.pages.marketingReportCheckoutPerformance'),
        'marketing#-C18gFCPnl',
        'MarketingReport',
        'ChartPie',
        ['checkout performance', 'checkout funnel', 'conversion'],
    ),
    reportPage(t('globalSearch.pages.marketingReportEmails'), 'marketing#5xCrsM0cH0', 'MarketingReport', 'ChartPie', [
        'emails report',
        'email performance',
        'open rate',
    ]),
    reportPage(t('globalSearch.pages.checkoutFunnelReport'), 'checkout_funnel/', 'CheckoutFunnelReport', 'Filter', [
        'funnel',
        'conversion',
        'checkout',
    ]),
    reportPage(t('globalSearch.pages.emailsReport'), 'emails/', 'EmailsReport', 'MailOpen', ['emails', 'open rate']),
    reportPage(t('globalSearch.pages.barcodesReport'), 'barcodes/', 'BarcodesReport', 'Barcode', [
        'barcodes',
        'redemption',
        'scans',
    ]),
    reportPage(t('globalSearch.pages.discountCodesReport'), 'discount_codes/', 'DiscountCodesReport', 'TicketPercent', [
        'discounts',
        'codes',
        'coupons',
    ]),
    reportPage(t('globalSearch.pages.crmReport'), 'crm/', 'CRMReportPage', 'UsersRound', [
        'crm',
        'customers',
        'audience',
    ]),
    {
        ...reportPage(t('globalSearch.pages.engageToolsReport'), 'engage_tools/', 'EngageToolsReport', 'Megaphone', [
            'engage',
            'wonderbar',
        ]),
        permissions: ['view_wonderbar'],
    },
]

const getSettingsPages = (): DirectoryPage[] => [
    {
        title: t('globalSearch.pages.accountSettings'),
        keywords: ['settings', 'payment methods', 'integrations', 'tax', 'booking fees'],
        iconName: 'Settings2',
        path: accountPage('/settings/account_settings/'),
        permissions: ['partner_admin'],
    },
    {
        title: t('globalSearch.pages.script'),
        keywords: ['snippet', 'installation', 'tracking code', 'embed'],
        iconName: 'Code',
        path: accountPage('/settings/add_snippet/'),
        permissions: ['partner_admin'],
    },
    {
        title: t('globalSearch.pages.users'),
        keywords: ['team', 'permissions', 'invite user'],
        iconName: 'Users',
        path: accountPage('/settings/users/user_list'),
        permissions: ['partner_admin'],
    },
]

const getPersonalPages = (): DirectoryPage[] => [
    {
        title: t('globalSearch.pages.whatsNew'),
        keywords: ['release notes', 'updates', 'changelog'],
        iconName: 'Mailbox',
        path: accountPage('/release_notes/'),
        feature: 'release_notes_page',
        hideForReseller: true,
    },
    {
        title: t('globalSearch.pages.notifications'),
        keywords: ['feed', 'alerts'],
        iconName: 'Bell',
        path: accountPage('/notifications/feed'),
        hideForReseller: true,
    },
    {
        title: t('globalSearch.pages.profile'),
        keywords: ['personal settings', 'password', 'avatar'],
        iconName: 'UserPen',
        path: () => '/personal_settings/personal',
    },
    {
        title: t('globalSearch.pages.serviceApps'),
        keywords: ['api key', 'api access', 'token', 'integration', 'automation', 'client credentials', 'mcp'],
        iconName: 'KeyRound',
        path: () => '/personal_settings/service_apps',
    },
]

// Commands: action screens reachable by name. Rows navigate to the action
// UI — the palette itself never mutates anything.
const getCommandPages = (): DirectoryPage[] => [
    {
        title: t('globalSearch.pages.redeemABarcode'),
        keywords: ['redeem', 'redeemer', 'scan', 'check in', 'validate ticket'],
        iconName: 'ScanLine',
        path: accountPage('/orders/orders/redeemMode'),
        permissions: ['access_orders'],
        hint: t('globalSearch.pages.actionHint'),
    },
    {
        title: t('globalSearch.pages.redeemAGroupBooking'),
        keywords: ['redeem group', 'group check in', 'group redeem'],
        iconName: 'Users',
        path: accountPage('/orders/orders/groupRedeemMode'),
        permissions: ['access_orders'],
        hint: t('globalSearch.pages.actionHint'),
    },
    {
        title: t('globalSearch.pages.exportOrders'),
        keywords: ['export', 'download orders', 'csv', 'excel'],
        iconName: 'Download',
        path: accountPage('/orders/orders/export'),
        permissions: ['access_orders'],
        hint: t('globalSearch.pages.actionHint'),
    },
]

// Built per call so titles are translated when searched, not when this module loads.
export const getPageDirectory = (): DirectoryPage[] => [
    ...getDashboardPages(),
    ...getOrderPages(),
    ...getProductPages(),
    ...getVenuePages(),
    ...getSalesPages(),
    ...getEngagePages(),
    ...getReportPages(),
    ...getSettingsPages(),
    ...getPersonalPages(),
    ...getCommandPages(),
]

export const MAX_PAGE_RESULTS = 5

// Title matches rank above keyword matches; prefix matches rank above substring.
const matchScore = (page: DirectoryPage, query: string): number => {
    const title = page.title.toLowerCase()
    if (title.startsWith(query)) {
        return 4
    }
    if (title.includes(query)) {
        return 3
    }
    if (page.keywords.some((keyword) => keyword.startsWith(query))) {
        return 2
    }
    if (page.keywords.some((keyword) => keyword.includes(query))) {
        return 1
    }
    return 0
}

export const searchPages = (
    query: string,
    accountSlug: string,
    canAccess: (page: DirectoryPage) => boolean,
): PageResult[] =>
    getPageDirectory()
        .map((page) => ({ page, score: matchScore(page, query.toLowerCase()) }))
        .filter(({ page, score }) => score > 0 && canAccess(page))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PAGE_RESULTS)
        .map(({ page }) => ({
            title: page.title,
            iconName: page.iconName,
            path: page.path(accountSlug),
            hint: page.hint,
        }))
